import { TranslationKey } from '../../idscan/enums/Translation';
import { getPlatform } from '../functions/getPlatform';
import { ExceptionManager } from './ExceptionManager';

export interface ISelectItem {
	name: string;
	value: string;
	selected?: boolean;
	disabled?: boolean;
}

/**
 * @internal
 */
export class RenderingService {

	constructor() {
		if (getPlatform() === 'NODE') {
			throw new Error('This feature cannot be used outside of a browser');
		}
	}

	/**
	 * Takes an id and returns tries to find a DOM element with that id
	 * @param id
	 */
	public getElement = <T = HTMLElement>(id: string): T | undefined => {
		id = id.startsWith('#') ? id.substr(1) : id;
		const element = document.getElementById(id);

		if (element) {
			return (element as any) as T;
		} else {
			console.debug(`Element #${id} is not found.`);

			return;
		}
	}

	/**
	 * Takes an id and returns tries to find a DOM element with that id
	 * @param id
	 */
	public getElementBySelector = <T = HTMLElement>(selector: string): T | undefined => {
		return (Array.from(document.querySelectorAll(selector))[0] as any) as T | undefined;
	}

	/**
	 * Takes a url and returns the file at the url
	 * @param url
	 */
	public getFile = <T = any>(url: string): Promise<T> => {
		return new Promise((resolve, reject) => {
			if (typeof XMLHttpRequest !== 'undefined') {
				const request = new XMLHttpRequest();
				request.open('GET', url);
				request.onreadystatechange = () => {
					if (request.status === 200 && request.readyState === 4) {
						resolve(request.response);
					}
				};

				request.onerror = (err) => reject(err);
				request.send();
			} else {
				reject(ExceptionManager.process(new ExceptionManager.PlatformException(TranslationKey.EXCEPTION_PLATFORM_BROWSER_ONLY)));
			}
		});
	}

	public hideObject = <T extends HTMLElement>(el?: T, type: string = 'none'): T | undefined => {
		if (el) {
			el.style.display = type;
		}

		return el;
	}

	public showObject = <T extends HTMLElement>(el?: T, type: string = ''): T | undefined => {
		if (el) {
			el.style.display = type;
		}

		return el;
	}

	public addClass = <T extends HTMLElement>(el?: T, name: string | string[] = []): T | undefined => {
		if (el) {
			el.classList.add(...(name instanceof Array ? name : [name]));
		}

		return el;
	}

	public removeClass = <T extends HTMLElement>(el?: T, name: string | string[] = []): T | undefined => {
		if (el) {
			el.classList.remove(...(name instanceof Array ? name : [name]));
		}

		return el;
	}

	public markSelected = <T extends HTMLSelectElement>(el?: T, value: string = ''): T | undefined => {
		if (el) {
			el.value = value;
		}

		return el;
	}

	public updateSelect = <T extends HTMLSelectElement>(el?: T, values: ISelectItem[] = []): T | undefined => {
		if (el) {
			// First clear the contents of the box
			el.options.length = 0;

			// Then for each specified option, add in a new element
			values.forEach(({ name, value, selected = false, disabled = false }, i) => {
				const option = new Option(name, value, i === 0, selected);
				option.disabled = disabled;
				el.options.add(option);
			});
		}

		return this.markSelected(el, (values.find((value) => Boolean(value.selected)) || { value: undefined }).value);
	}

	public supportsDragDrop = (): boolean => {
		return 'files' in DataTransfer.prototype;
	}

	public setText = <T extends HTMLElement>(el?: T, value: string = ''): T | undefined => {
		if (el) {
			el.innerText = value;
			this.showObject(el);
		}

		return el;
	}

	public setValue = <T extends HTMLFormElement | HTMLInputElement>(el?: T, value: string = ''): T | undefined => {
		if (el) {
			el.value = value;
			this.showObject(el);
		}

		return el;
	}

	public setEnabled = <T extends HTMLInputElement | HTMLButtonElement | HTMLSelectElement>(el?: T, state: boolean = true): T | undefined => {
		if (el) {
			if (state) {
				el.removeAttribute('disabled');
			} else {
				el.setAttribute('disabled', 'disabled');
			}
		}

		return el;
	}

	public setDataAttributeValue = <T extends HTMLElement>(el?: T, attribute: string = '', value: string | number = ''): T | undefined => {
		if (el) {
			el.setAttribute(`data-${attribute}`, value.toString());
		}

		return el;
	}

}

export default new RenderingService();
